import React, { createContext, FC, ReactNode, useContext, useEffect, useRef, useState } from 'react';
import { useNavigate } from 'react-router';
import { HotelOption } from '../components/HotelRedirectHandler';
import { Loading } from '../components/Loading';
import { usePluginspace } from '../hooks/usePluginspace';
import api from '../services/api';
import { parseIsCepRequired, normalizeHotelCep, decodeHotelAddressParam, buildHotelAddressFromLocation } from '../functions/util';

type HotelHandlerContextType = {
    hotelReservationContext: any;
};




const HotelHandlerContext = createContext<HotelHandlerContextType>({} as HotelHandlerContextType);

type HotelHandlerContextProviderProps = {
    children: ReactNode;
};

const HotelHandlerProvider: FC<HotelHandlerContextProviderProps> = (props) => {
    const { children } = props;
    const hotelReservationContext = {}; // Initialize your context value here
    const navigate = useNavigate();
    const { getToken, pluginspace } = usePluginspace();
    const [loading, setLoading] = useState(true);
    const hasProcessed = useRef(false);
    const [, setHotelOptions] = useState<HotelOption[]>([]);
    const [, setShowModal] = useState(false);

    useEffect(() => {
        const run = async () => {
            // Evita reprocessamento se já foi executado
            if (hasProcessed.current) return;

            // Verificar se já existe contexto salvo
            const existingContext = sessionStorage.getItem("hotelReservationContext");
            if (existingContext) {
                hasProcessed.current = true;
                return;
            }

            // Verificar se pluginspace está disponível
            if (!pluginspace?.id) {
                return; // Aguardar pluginspace carregar
            }

            // IMPORTANTE: Setar flag IMEDIATAMENTE antes de qualquer operação assíncrona
            hasProcessed.current = true;

            const params = new URLSearchParams(window.location.search);

            // Only accept explicit reservation + father (pluginspace) and guest token
            const reservation =
                params.get("reservation") || params.get("reservationId") || null;
            const father =
                params.get("father") ||
                params.get("pluginspace") ||
                params.get("pluginspaceId") ||
                null;
            const integration = params.get("integration") || null;
            const isCepRequired = parseIsCepRequired(params.get("isCepRequired"));
            const hotelCepFromUrl = normalizeHotelCep(
                params.get("hotelCep") || params.get("cep")
            );
            const hotelAddressFromUrl = decodeHotelAddressParam(
                params.get("hotelAddress")
            );
            const guestNameFromUrl = params.get("guestName") || undefined;
            const guestPhoneFromUrl = params.get("guestPhone") || undefined;
            const roomNumberFromUrl = params.get("roomNumber") || undefined;
            const token = await getToken();

            if (father && !reservation && token && !integration) {
                try {
                    const pluginspaceResp = await api.post(
                        `/api/store-relations/parents`,
                        {
                            child: pluginspace.id,
                            hasLink: true,
                            father: father ? parseInt(father) : undefined,
                        },
                        { headers: { Authorization: `Bearer ${token}` } }
                    );

                    if (pluginspaceResp?.data?.data) {
                        const hotels: HotelOption[] = pluginspaceResp.data.data.map(
                            (item: any) => ({
                                pluginspaceId: item.parentPluginspace.id,
                                name: item.parentPluginspace.name,
                                subdomain: item.parentPluginspace.subdomain,
                                image: item.storesHotel?.img || "",
                                description: item.parentPluginspace.type || "",
                                paymentIntegrated: item.storesHotel?.paymentIntegrated || false,
                                isCepRequired: parseIsCepRequired(item.storesHotel?.isCepRequired),
                                hotelCep: normalizeHotelCep(
                                    item.parentPluginspace?.address?.cep
                                ),
                                hotelAddress: buildHotelAddressFromLocation(
                                    item.parentPluginspace?.address
                                ),
                            })
                        );

                        // Se vier apenas 1 hotel, selecionar automaticamente
                        if (hotels.length === 1) {
                            const selectedHotel = hotels[0];
                            const viewOnlyContext = {
                                pluginspaceId: selectedHotel.pluginspaceId,
                                viewOnly: !selectedHotel.paymentIntegrated,
                                subdomain: selectedHotel.subdomain,
                                hotelSelectedByUser: true,
                                isCepRequired: selectedHotel.isCepRequired ?? isCepRequired,
                                hotelCep: hotelCepFromUrl || selectedHotel.hotelCep,
                                hotelAddress: hotelAddressFromUrl || selectedHotel.hotelAddress,
                                guestName: guestNameFromUrl,
                                guestPhone: guestPhoneFromUrl,
                                roomNumber: roomNumberFromUrl,
                            };

                            sessionStorage.setItem(
                                "hotelReservationContext",
                                JSON.stringify(viewOnlyContext)
                            );
                        } else if (hotels.length > 1) {
                            setHotelOptions(hotels);
                            setShowModal(true);
                        }
                    }
                } catch (e) {
                    console.warn("Could not fetch parent hotels with father param", e);
                }
                return;
            }

            // Processar quando vier reservation + father
            if (!reservation || !father) {
                return;
            }

            try {
                // Call server POST find-by-id with body { id, pluginspaceId }
                const resp = await api.post(
                    "/api/hotel-reservation/find-by-id",
                    { reservationId: reservation, pluginspaceId: father },
                    { headers: { Authorization: `Bearer ${token}` } }
                );

                if (resp && resp.data) {
                    // pick only minimal fields
                    const raw = resp.data.data ? resp.data.data : resp.data;
                    const candidate = Array.isArray(raw) ? raw[0] : raw;
                    const pick = (ctx: any) => {
                        if (!ctx || typeof ctx !== "object") return undefined;
                        const out: any = {};

                        // reservation id
                        if (ctx.id) out.reservationId = ctx.id;

                        // customer document: prefer customer.doc, fallback to cpf-like fields
                        const customerDoc =
                            ctx.customer?.doc ||
                            ctx.customer?.cpf ||
                            ctx.customer_doc ||
                            ctx.customerDoc ||
                            ctx.cpf;
                        if (customerDoc) out.doc = customerDoc;

                        if (father) out.pluginspaceId = father;

                        const subdomain = ctx.pluginspace.subdomain;
                        if (subdomain) out.subdomain = subdomain;

                        // room id: prefer nested room.id, fallback to top-level roomId/room_id
                        const roomId = ctx.room?.id ?? ctx.roomId ?? ctx.room_id;
                        if (roomId) out.roomId = roomId;

                        if (integration === "false" || !integration) {
                            out.viewOnly = true;
                        }

                        out.isCepRequired = isCepRequired;
                        if (hotelCepFromUrl) out.hotelCep = hotelCepFromUrl;
                        else {
                            const cepFromReservation = normalizeHotelCep(
                                ctx.pluginspace?.address?.cep
                            );
                            if (cepFromReservation) out.hotelCep = cepFromReservation;
                        }

                        const addressFromReservation = buildHotelAddressFromLocation(
                            ctx.pluginspace?.address
                        );
                        out.hotelAddress = hotelAddressFromUrl || addressFromReservation;
                        if (guestNameFromUrl) out.guestName = guestNameFromUrl;
                        else if (ctx.customer?.name) out.guestName = ctx.customer.name;
                        if (guestPhoneFromUrl) out.guestPhone = guestPhoneFromUrl;
                        else if (ctx.customer?.phone) out.guestPhone = ctx.customer.phone;
                        if (roomNumberFromUrl) out.roomNumber = roomNumberFromUrl;
                        else if (ctx.room?.roomNumber) out.roomNumber = String(ctx.room.roomNumber);

                        return Object.keys(out).length > 0 ? out : undefined;
                    };

                    const minimal = pick(candidate.dados[0]);
                    if (minimal) {
                        // Save to sessionStorage (frontend fallback)
                        try {
                            sessionStorage.setItem(
                                "hotelReservationContext",
                                JSON.stringify(minimal)
                            );
                        } catch (e) {
                            console.warn(
                                "Could not save hotelReservationContext to sessionStorage",
                                e
                            );
                        }

                        // server-side session endpoint not present; only persist to sessionStorage
                    }
                }
            } catch (err: unknown) {
                const msg =
                    err && typeof err === "object" && "message" in err
                        ? (err as any).message
                        : String(err);
                console.warn("hotel context save failed", msg);
            }

            // remove only the token/reservation/father params to avoid leaking them
            params.delete("guest_token");
            params.delete("token_id");
            params.delete("token");
            params.delete("reservation");
            params.delete("reservationId");
            params.delete("father");
            params.delete("pluginspace");
            params.delete("pluginspaceId");
            params.delete("integration");
            params.delete("isCepRequired");
            params.delete("hotelCep");
            params.delete("cep");
            params.delete("hotelAddress");
            params.delete("guestName");
            params.delete("guestPhone");
            params.delete("roomNumber");

            const newSearch = params.toString();
            // Do NOT modify the path; keep any path segment intact.
            const newUrl =
                window.location.pathname +
                (newSearch ? `?${newSearch}` : "") +
                window.location.hash;
            window.history.replaceState({}, document.title, newUrl);
        };

        run().catch((e) => {
            console.warn("HotelRedirectHandler error", e);
            // Reset flag em caso de erro para permitir retry
            hasProcessed.current = false;
        }).finally(() => {
            setTimeout(() => {
                setLoading(false);
            }, 1000); // Pequeno delay para evitar flicker caso o processamento seja muito rápido
        });
    }, [navigate, getToken, pluginspace?.id]);

    if (loading) {
        return (
            <div
                style={{
                    height: "100vh",
                }}
            >
                <Loading type="spinningBubbles" color="#FFF" width={64} height={64} />
            </div>
        );
    }

    return (
        <HotelHandlerContext.Provider value={{ hotelReservationContext }}>
            {children}
        </HotelHandlerContext.Provider>
    );
}

export default HotelHandlerProvider;

export const useHotelHandlerContext = () => {
    return useContext(HotelHandlerContext);
}
